import { useDriverDims } from './useDriverDims'
import { resolveDriverArmFilters, type DriverArmFilters } from '~/pin/driverArmFilters'

/** FU-27(a): the ONE driver-arm resolution for ChartFromPin's two driver arms — wraps
 *  useDriverDims and resolves base + per-timeframe filters with each per-tf resolution computed
 *  exactly ONCE (driverTimeframes and campaignBlocked both derive from the same results; the
 *  pre-fold code recomputed resolveDriverArmFilters inside a `.some`). Generic over the two
 *  timeframe shapes (viz_params entries / DynamicsTimeframe). Semantics are byte-identical to
 *  the folded blocks — pinned by the existing FU-03 suites.
 *
 *  FU-27(b) — documented limitation, kept (D-W-2): campaignBlocked keys on the BASE resolution
 *  too, even when every per-arm tf carries its own filter_dims override; a base-blocked pin
 *  whose arms all override could in theory diverge chart-note vs xlsx rows. Theoretical (needs
 *  every arm to override AND per-arm blocking to differ from base); revisit only if a real pin
 *  hits it. */
export function useDriverArmResolution<T extends { filter_dims?: Record<string, string[]> }>(
  driverFact: string,
  hasDriver: boolean,
  baseFilterDims: Record<string, string[]>,
  timeframes: T[],
): {
  driverGate: boolean
  baseDriver: DriverArmFilters | null
  driverTimeframes: T[]
  campaignBlocked: boolean
} {
  const { dims: driverDims, pending: driverGate } = useDriverDims(driverFact, hasDriver)
  const baseDriver = driverDims ? resolveDriverArmFilters(baseFilterDims, driverDims) : null
  const perTf = driverDims
    ? timeframes.map((tf) => ({
        tf,
        res: tf.filter_dims ? resolveDriverArmFilters(tf.filter_dims, driverDims) : null,
      }))
    : null
  const driverTimeframes = perTf
    ? perTf.map(({ tf, res }) => (res ? { ...tf, filter_dims: res.filters } : tf))
    : timeframes
  const campaignBlocked =
    hasDriver &&
    driverDims !== null &&
    (baseDriver!.campaignBlocked || (perTf ?? []).some(({ res }) => res?.campaignBlocked ?? false))
  return { driverGate, baseDriver, driverTimeframes, campaignBlocked }
}
